"use client";

import { InfoIcon, NetworkIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import type { AuthorityTierKey, CommonCrawlRankResponse } from "../lib/api";
import { formatRank, formatTopPercent } from "../lib/format";

interface CommonCrawlRankResultsProps {
	result: CommonCrawlRankResponse;
}

/** Colors per authority tier: green (elite) grading down to amber, then red for unranked. */
const tierColors: Record<AuthorityTierKey, string> = {
	elite: "#059669",
	authoritative: "#16a34a",
	established: "#0d9488",
	strong: "#2563eb",
	emerging: "#d97706",
	unranked: "#dc2626",
};

/** A small emoji per tier for a touch of color and recognition. */
const tierEmoji: Record<AuthorityTierKey, string> = {
	elite: "🏆",
	authoritative: "⭐",
	established: "✅",
	strong: "📈",
	emerging: "🌱",
	unranked: "🧭",
};

const TIER_LADDER: AuthorityTierKey[] = [
	"elite",
	"authoritative",
	"established",
	"strong",
	"emerging",
	"unranked",
];

function formatDate(iso: string): string {
	const date = new Date(iso);
	return date.toLocaleDateString("en-US", {
		year: "numeric",
		month: "long",
		day: "numeric",
	});
}

interface MetricCardProps {
	label: string;
	rank: number | null;
	trackedCount: number;
	notAvailable: string;
	color: string;
	topPercentTemplate: (value: string) => string;
	rankLine: (rank: string) => string;
}

/** One authority metric: a large "Top X%" figure with the ordinal rank below it. */
function MetricCard({
	label,
	rank,
	trackedCount,
	notAvailable,
	color,
	topPercentTemplate,
	rankLine,
}: MetricCardProps) {
	const topPercent = formatTopPercent(rank, trackedCount);
	return (
		<div
			className="text-center p-5 rounded-lg border-t-4"
			style={{
				borderTopColor: color,
				backgroundColor: `${color}0d`,
			}}
		>
			<div
				className="text-4xl font-bold"
				style={topPercent != null ? { color } : undefined}
			>
				{topPercent != null
					? topPercentTemplate(topPercent)
					: notAvailable}
			</div>
			<div className="text-sm font-medium text-muted-foreground mt-2">
				{label}
			</div>
			{rank != null && (
				<div className="text-xs text-muted-foreground/70 mt-1.5">
					{rankLine(formatRank(rank))}
				</div>
			)}
		</div>
	);
}

export function CommonCrawlRankResults({
	result,
}: CommonCrawlRankResultsProps) {
	const t = useTranslations();
	const { domain, record, tier, metadata } = result;
	const found = record !== null;
	const tierColor = tierColors[tier.key];

	// We rank against the tracked set (top ~10M by Harmonic Centrality), not the
	// full graph, so the percentile matches the tier labels. Fall back to 10M if
	// metadata hasn't been synced.
	const trackedCount = metadata.loadedRows ?? 10_000_000;

	// Provenance line: assemble only the segments we actually have.
	const provenanceSegments: string[] = [];
	if (metadata.releaseId) {
		provenanceSegments.push(
			t("marketing.commonCrawlRankChecker.results.provenanceRelease", {
				release: metadata.releaseId,
			}),
		);
	}
	if (metadata.loadedRows != null) {
		provenanceSegments.push(
			t("marketing.commonCrawlRankChecker.results.provenanceTracked", {
				count: metadata.loadedRows.toLocaleString("en-US"),
			}),
		);
	}
	if (metadata.updatedAt) {
		provenanceSegments.push(
			t("marketing.commonCrawlRankChecker.results.provenanceUpdated", {
				date: formatDate(metadata.updatedAt),
			}),
		);
	}

	const trackedLabel =
		metadata.loadedRows != null
			? metadata.loadedRows.toLocaleString("en-US")
			: "10,000,000";

	const topPercentTemplate = (value: string) =>
		t("marketing.commonCrawlRankChecker.results.topPercent", {
			percent: value,
		});
	const rankLine = (rank: string) =>
		t("marketing.commonCrawlRankChecker.results.rankLine", { rank });

	return (
		<div className="space-y-6">
			{/* Header with tier */}
			<div className="text-center space-y-4">
				<div
					className="inline-flex items-center gap-2 px-4 py-2 rounded-full border"
					style={{
						backgroundColor: `${tierColor}1a`,
						borderColor: `${tierColor}40`,
					}}
				>
					<span className="text-lg leading-none" aria-hidden="true">
						{tierEmoji[tier.key]}
					</span>
					<span
						className="font-semibold"
						style={{ color: tierColor }}
					>
						{t(
							`marketing.commonCrawlRankChecker.tiers.${tier.key}.label`,
						)}
					</span>
				</div>

				<div>
					<h3 className="text-2xl font-bold">{domain}</h3>
					<p className="text-muted-foreground mt-1 max-w-xl mx-auto">
						{t(
							`marketing.commonCrawlRankChecker.tiers.${tier.key}.description`,
						)}
					</p>
				</div>
			</div>

			{/* Tier ladder */}
			<div className="flex items-center justify-center gap-1.5 flex-wrap">
				{TIER_LADDER.map((key) => {
					const isActive = key === tier.key;
					return (
						<div
							key={key}
							className={`px-3 h-9 flex items-center gap-1.5 justify-center rounded-md text-xs font-semibold transition-all ${
								isActive ? "scale-105 text-white shadow-md" : ""
							}`}
							style={
								isActive
									? { backgroundColor: tierColors[key] }
									: {
											backgroundColor: `${tierColors[key]}14`,
											color: tierColors[key],
										}
							}
						>
							<span
								className="text-sm leading-none"
								aria-hidden="true"
							>
								{tierEmoji[key]}
							</span>
							{t(
								`marketing.commonCrawlRankChecker.tiers.${key}.label`,
							)}
						</div>
					);
				})}
			</div>

			{/* Main results card */}
			<div className="bg-card rounded-xl border p-6 space-y-4">
				{found ? (
					<>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							{/* Harmonic Centrality */}
							<MetricCard
								label={t(
									"marketing.commonCrawlRankChecker.results.hcLabel",
								)}
								rank={record.rank}
								trackedCount={trackedCount}
								notAvailable={t(
									"marketing.commonCrawlRankChecker.results.notAvailable",
								)}
								color={tierColor}
								topPercentTemplate={topPercentTemplate}
								rankLine={rankLine}
							/>

							{/* PageRank */}
							<MetricCard
								label={t(
									"marketing.commonCrawlRankChecker.results.pageRankLabel",
								)}
								rank={record.pageRankRank}
								trackedCount={trackedCount}
								notAvailable={t(
									"marketing.commonCrawlRankChecker.results.notAvailable",
								)}
								color={tierColor}
								topPercentTemplate={topPercentTemplate}
								rankLine={rankLine}
							/>
						</div>

						<p className="text-center text-xs text-muted-foreground/70">
							{t(
								"marketing.commonCrawlRankChecker.results.lowerIsBetter",
							)}
						</p>
					</>
				) : (
					/* Not-found state */
					<div className="flex items-start gap-3 rounded-lg border bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 p-4">
						<NetworkIcon className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
						<div>
							<h4 className="font-semibold text-amber-800 dark:text-amber-300 mb-1">
								{t(
									"marketing.commonCrawlRankChecker.results.notFoundTitle",
								)}
							</h4>
							<p className="text-sm text-amber-700 dark:text-amber-300">
								{t(
									"marketing.commonCrawlRankChecker.results.notFoundBody",
									{
										count: trackedLabel,
									},
								)}
							</p>
						</div>
					</div>
				)}
			</div>

			{/* Dataset provenance */}
			{provenanceSegments.length > 0 && (
				<p className="text-center text-xs text-muted-foreground flex items-center justify-center gap-1.5">
					<InfoIcon className="w-3.5 h-3.5" />
					{provenanceSegments.join(" · ")}
				</p>
			)}
		</div>
	);
}
